import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Typography,
  Alert,
  FormHelperText,
  CircularProgress
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import { useRoleDefinitions } from '../../hooks/useRoleDefinitions';
import { checkRoleCodeUniqueness } from '../../services/roleDefinitionService';
import { usePeopleAdmin } from '../../context/PeopleAdminContext';
import { fetchHierarchyDefinitions } from '../../services/hierarchyService';

const validationSchema = Yup.object({
  roleName: Yup.string()
    .required('Role name is required')
    .min(2, 'Role name must be at least 2 characters')
    .max(50, 'Role name must be less than 50 characters'),
  roleCode: Yup.string()
    .required('Role code is required')
    .matches(/^[A-Z0-9_]+$/, 'Role code must be uppercase letters, numbers, or underscores')
    .min(2, 'Role code must be at least 2 characters')
    .max(10, 'Role code must be less than 10 characters'),
  assignmentLevel: Yup.string()
    .oneOf(['LOCATION_LEVEL', 'ORGLEVEL_1', 'ORGLEVEL_2', 'ORGLEVEL_3'], 'Invalid assignment level')
    .required('Assignment level is required'),
  category: Yup.string()
    .when('assignmentLevel', {
      is: 'LOCATION_LEVEL',
      then: (schema) => schema.oneOf(['FOH', 'BOH', 'MANAGEMENT', 'OTHER'], 'Invalid role category').required('Role category is required'),
      otherwise: (schema) => schema.oneOf(['MANAGEMENT', 'OTHER'], 'Invalid role category').nullable()
    }),
  isActive: Yup.boolean(),
  isDefault: Yup.boolean()
});

/**
 * Form component for creating and editing role definitions
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback when dialog closes
 * @param {Object} props.roleDefinition - Role definition to edit (null for create)
 * @param {Function} props.onSuccess - Callback on successful save
 * @returns {React.Component} Role definition form dialog
 */
function RoleDefinitionForm({ open, onClose, roleDefinition, onSuccess }) {
  const { clientId } = usePeopleAdmin();
  const { createRole, updateRole, isCreating, isUpdating } = useRoleDefinitions();
  const [codeError, setCodeError] = React.useState('');
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);

  useEffect(() => {
    if (open && clientId) {
      setLoadingHierarchy(true);
      fetchHierarchyDefinitions(clientId)
        .then(levels => {
          // Only use the first 3 org levels as per the schema (ORGLEVEL_1, ORGLEVEL_2, ORGLEVEL_3)
          const validLevels = levels
            .filter(level => ['ORGLEVEL_1', 'ORGLEVEL_2', 'ORGLEVEL_3'].includes(level.levelId))
            .slice(0, 3);
          
          // Only set hierarchy levels if we got valid ones
          if (validLevels.length > 0) {
            setHierarchyLevels(validLevels);
          }
          // If no valid levels, keep the empty array - the Select will show the current value
        })
        .catch(error => {
          console.error('Failed to load hierarchy levels:', error);
          // Keep the empty array - the Select will show the current value as fallback
        })
        .finally(() => setLoadingHierarchy(false));
    }
  }, [open, clientId]);

  const isEdit = Boolean(roleDefinition);

  const initialValues = {
    roleName: roleDefinition?.roleName || '',
    roleCode: roleDefinition?.roleCode || '',
    assignmentLevel: roleDefinition?.assignmentLevel || 'LOCATION_LEVEL',
    category: roleDefinition?.category || '',
    isActive: roleDefinition?.isActive !== false,
    isDefault: roleDefinition?.isDefault || false
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    console.log('RoleDefinitionForm - handleSubmit called with values:', values);
    console.log('RoleDefinitionForm - isEdit:', isEdit);
    console.log('RoleDefinitionForm - clientId:', clientId);
    
    try {
      // Check role code uniqueness
      const isUnique = await checkRoleCodeUniqueness(
        values.roleCode,
        clientId,
        isEdit ? roleDefinition.id : null
      );

      if (!isUnique) {
        setErrors({ roleCode: 'This role code already exists' });
        setSubmitting(false);
        return;
      }

      const roleData = {
        roleName: values.roleName,
        roleCode: values.roleCode.toUpperCase(),
        assignmentLevel: values.assignmentLevel,
        category: values.category || null, // Keep the category value, allow null if empty
        sortOrder: 0, // Default to 0, will sort alphabetically in UI
        isActive: values.isActive,
        isDefault: values.isDefault,
        metadata: null // Set to null instead of empty object
      };
      
      console.log('RoleDefinitionForm - roleData prepared:', roleData);
      console.log('RoleDefinitionForm - category from values:', values.category);
      console.log('RoleDefinitionForm - assignmentLevel:', values.assignmentLevel);

      if (isEdit) {
        // Include clientId for updates to ensure proper GraphQL mutation
        console.log('RoleDefinitionForm - Updating role with:', {
          id: roleDefinition.id,
          updates: { ...roleData, clientId },
          originalRole: roleDefinition
        });
        await updateRole({ id: roleDefinition.id, updates: { ...roleData, clientId } });
        console.log('RoleDefinitionForm - Update completed successfully');
      } else {
        console.log('RoleDefinitionForm - Creating role with:', roleData);
        await createRole(roleData);
        console.log('RoleDefinitionForm - Create completed successfully');
      }

      // Call onSuccess after mutation completes
      if (onSuccess) {
        onSuccess();
      }
      
      // Close the form after everything is done
      onClose();
    } catch (error) {
      console.error('Failed to save role definition:', error);
      setErrors({ submit: error.message });
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        {isEdit ? 'Edit Role Definition' : 'Create Role Definition'}
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue, isValid }) => {
          // Check if all required fields have values
          const isFormComplete = values.roleName && 
                                 values.roleCode && 
                                 values.assignmentLevel && 
                                 (values.assignmentLevel === 'LOCATION_LEVEL' ? values.category : true);
          
          return (
          <Form>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="roleName"
                    label="Role Name"
                    value={values.roleName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.roleName && Boolean(errors.roleName)}
                    helperText={touched.roleName && errors.roleName}
                    placeholder="e.g., Store Manager"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="roleCode"
                    label="Role Code"
                    value={values.roleCode}
                    onChange={(e) => {
                      setFieldValue('roleCode', e.target.value.toUpperCase());
                      setCodeError('');
                    }}
                    onBlur={handleBlur}
                    error={touched.roleCode && Boolean(errors.roleCode || codeError)}
                    helperText={touched.roleCode && (errors.roleCode || codeError)}
                    placeholder="e.g., SM"
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={touched.category && Boolean(errors.category)}>
                    <InputLabel>Role Category</InputLabel>
                    <Select
                      name="category"
                      value={values.category || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Role Category"
                      sx={{ height: 40 }}
                    >
                      {values.assignmentLevel === 'LOCATION_LEVEL' && (
                        <MenuItem value="FOH">Front of House</MenuItem>
                      )}
                      {values.assignmentLevel === 'LOCATION_LEVEL' && (
                        <MenuItem value="BOH">Back of House</MenuItem>
                      )}
                      <MenuItem value="MANAGEMENT">Management</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                    </Select>
                    {touched.category && errors.category && (
                      <FormHelperText error>{errors.category}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={touched.assignmentLevel && Boolean(errors.assignmentLevel)}>
                    <InputLabel>Assignment Level</InputLabel>
                    <Select
                      name="assignmentLevel"
                      value={values.assignmentLevel}
                      onChange={(e) => {
                        handleChange(e);
                        // Update category based on assignment level
                        if (e.target.value !== 'LOCATION_LEVEL') {
                          // If current category is FOH or BOH, change to MANAGEMENT
                          if (values.category === 'FOH' || values.category === 'BOH') {
                            setFieldValue('category', 'MANAGEMENT');
                          }
                        }
                      }}
                      onBlur={handleBlur}
                      label="Assignment Level"
                      disabled={loadingHierarchy}
                      sx={{ height: 40 }}
                    >
                      <MenuItem value="LOCATION_LEVEL">Location</MenuItem>
                      {/* Show current value if it's an org level and hierarchy hasn't loaded yet */}
                      {values.assignmentLevel && values.assignmentLevel !== 'LOCATION_LEVEL' && 
                       hierarchyLevels.length === 0 && (
                        <MenuItem value={values.assignmentLevel}>
                          {values.assignmentLevel}
                        </MenuItem>
                      )}
                      {/* Show loaded hierarchy levels */}
                      {hierarchyLevels.map(level => (
                        <MenuItem key={level.levelId} value={level.levelId}>
                          {level.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {loadingHierarchy && (
                      <FormHelperText>
                        <CircularProgress size={12} /> Loading hierarchy levels...
                      </FormHelperText>
                    )}
                    {touched.assignmentLevel && errors.assignmentLevel && (
                      <FormHelperText error>{errors.assignmentLevel}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>


                <Grid item xs={12}>
                  <Box display="flex" gap={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="isActive"
                          checked={values.isActive}
                          onChange={handleChange}
                        />
                      }
                      label="Active"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          name="isDefault"
                          checked={values.isDefault}
                          onChange={handleChange}
                        />
                      }
                      label="Default Role"
                    />
                  </Box>
                </Grid>

                {errors.submit && (
                  <Grid item xs={12}>
                    <Alert severity="error">{errors.submit}</Alert>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
              <AdminButton onClick={onClose} variant="outlined">
                Cancel
              </AdminButton>
              <AdminButton
                type="submit"
                variant="contained"
                disabled={!isFormComplete || isSubmitting || isCreating || isUpdating}
              >
                {isSubmitting || isCreating || isUpdating
                  ? 'Saving...'
                  : isEdit
                  ? 'Update Role'
                  : 'Create Role'}
              </AdminButton>
            </DialogActions>
          </Form>
          )
        }}
      </Formik>
    </Dialog>
  );
}

RoleDefinitionForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roleDefinition: PropTypes.object,
  onSuccess: PropTypes.func
};

export default RoleDefinitionForm;